import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Save, X, ChevronDown, ChevronUp, Calendar, Clock, User, Users, Layers, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const LEVEL_OPTIONS = ['All Levels', 'Beginner', 'Intermediate', 'Advanced'];
const LEVEL_COLORS = {
  'All Levels': '#6366f1',
  'Beginner': '#22c55e',
  'Intermediate': '#f59e0b',
  'Advanced': '#ef4444',
};
const DEFAULT_COLORS = ['#4f46e5', '#7c3aed', '#059669', '#dc2626', '#d97706', '#0891b2', '#be185d'];

const emptyForm = {
  name: '',
  days: '',
  time: '',
  instructor: '',
  description: '',
  capacity: '',
  level: 'All Levels',
  color: '#4f46e5',
  is_active: true,
};

export default function ClassSchedulePanel({ websiteId, token, onSchedulesChanged }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingId, setEditingId] = useState(null); // null = not editing, 'new' = adding
  const [form, setForm] = useState(emptyForm);
  const [expandedId, setExpandedId] = useState(null);

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(null), 5000);
  };

  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  }), [token]);

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/websites/${websiteId}/schedules/all`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSchedules(data);
    } catch (err) {
      showError('Failed to load schedules: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [websiteId, authHeaders]);

  useEffect(() => {
    if (websiteId && token) fetchSchedules();
  }, [websiteId, token, fetchSchedules]);

  const handleSeed = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/websites/${websiteId}/schedules/seed`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      showSuccess(data.message || 'Seeded successfully');
      fetchSchedules();
      if (onSchedulesChanged) onSchedulesChanged();
    } catch (err) {
      showError('Seed failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    setEditingId('new');
    setForm({ ...emptyForm });
    setExpandedId(null);
  };

  const handleEdit = (schedule) => {
    setEditingId(schedule.id);
    setForm({
      name: schedule.name || '',
      days: schedule.days || '',
      time: schedule.time || '',
      instructor: schedule.instructor || '',
      description: schedule.description || '',
      capacity: schedule.capacity || '',
      level: schedule.level || 'All Levels',
      color: schedule.color || '#4f46e5',
      is_active: schedule.is_active !== false,
    });
    setExpandedId(null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.days.trim() || !form.time.trim()) {
      showError('Name, Days, and Time are required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        days: form.days.trim(),
        time: form.time.trim(),
        instructor: form.instructor.trim() || null,
        description: form.description.trim() || null,
        capacity: form.capacity ? parseInt(form.capacity) : null,
        level: form.level,
        color: form.color,
        is_active: form.is_active,
      };

      let res;
      if (editingId === 'new') {
        res = await fetch(`${API_BASE}/api/websites/${websiteId}/schedules`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${API_BASE}/api/websites/${websiteId}/schedules/${editingId}`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify(body),
        });
      }

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }

      showSuccess(editingId === 'new' ? 'Class added successfully!' : 'Class updated successfully!');
      setEditingId(null);
      setForm(emptyForm);
      fetchSchedules();
      if (onSchedulesChanged) onSchedulesChanged();
    } catch (err) {
      showError('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (scheduleId, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/websites/${websiteId}/schedules/${scheduleId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showSuccess(`"${name}" deleted`);
      fetchSchedules();
      if (onSchedulesChanged) onSchedulesChanged();
    } catch (err) {
      showError('Delete failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (schedule) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/websites/${websiteId}/schedules/${schedule.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ is_active: !schedule.is_active }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      fetchSchedules();
      if (onSchedulesChanged) onSchedulesChanged();
    } catch (err) {
      showError('Toggle failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full text-sm">
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-violet-400" />
          <span className="font-semibold text-white">Class Schedule</span>
          <span className="text-xs text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">{schedules.length}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={fetchSchedules}
            disabled={loading}
            className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {schedules.length === 0 && (
            <button
              onClick={handleSeed}
              disabled={saving}
              className="text-xs px-2 py-1 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded transition-colors"
              title="Seed default classes from template"
            >
              Seed Defaults
            </button>
          )}
          <button
            onClick={handleAdd}
            disabled={editingId !== null}
            className="flex items-center gap-1 text-xs px-2 py-1 bg-violet-600 hover:bg-violet-500 text-white rounded transition-colors disabled:opacity-50"
          >
            <Plus className="w-3 h-3" />
            Add Class
          </button>
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="mx-3 mt-2 px-3 py-2 bg-red-900/40 border border-red-500/40 rounded text-red-300 text-xs flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="mx-3 mt-2 px-3 py-2 bg-green-900/40 border border-green-500/40 rounded text-green-300 text-xs flex items-start gap-2">
          <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Add/Edit Form */}
      {editingId !== null && (
        <div className="mx-3 mt-3 p-3 bg-zinc-800 border border-violet-500/30 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-violet-300">
              {editingId === 'new' ? '+ New Class' : 'Edit Class'}
            </span>
            <button onClick={handleCancel} className="text-zinc-400 hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="space-y-2.5">
            {/* Name */}
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Class Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Morning Flow"
                className="w-full bg-zinc-700 border border-zinc-600 rounded px-2.5 py-1.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-violet-500"
              />
            </div>

            {/* Days */}
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Days *</label>
              <input
                type="text"
                value={form.days}
                onChange={e => setForm({ ...form, days: e.target.value })}
                placeholder="e.g. Monday, Wednesday & Friday"
                className="w-full bg-zinc-700 border border-zinc-600 rounded px-2.5 py-1.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-violet-500"
              />
            </div>

            {/* Time */}
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Time *</label>
              <input
                type="text"
                value={form.time}
                onChange={e => setForm({ ...form, time: e.target.value })}
                placeholder="e.g. 8:00 AM to 9:00 AM"
                className="w-full bg-zinc-700 border border-zinc-600 rounded px-2.5 py-1.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-violet-500"
              />
            </div>

            {/* Instructor */}
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Instructor</label>
              <input
                type="text"
                value={form.instructor}
                onChange={e => setForm({ ...form, instructor: e.target.value })}
                placeholder="e.g. Maya Ross"
                className="w-full bg-zinc-700 border border-zinc-600 rounded px-2.5 py-1.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-violet-500"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Brief class description..."
                rows={2}
                className="w-full bg-zinc-700 border border-zinc-600 rounded px-2.5 py-1.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-violet-500 resize-none"
              />
            </div>

            {/* Capacity + Level row */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Capacity</label>
                <input
                  type="number"
                  value={form.capacity}
                  onChange={e => setForm({ ...form, capacity: e.target.value })}
                  placeholder="Max participants"
                  min="1"
                  className="w-full bg-zinc-700 border border-zinc-600 rounded px-2.5 py-1.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-violet-500"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Level</label>
                <select
                  value={form.level}
                  onChange={e => setForm({ ...form, level: e.target.value })}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-violet-500"
                >
                  {LEVEL_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            </div>

            {/* Color picker */}
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Card Color</label>
              <div className="flex items-center gap-2">
                <div className="flex gap-1.5">
                  {DEFAULT_COLORS.map(c => (
                    <button
                      key={c}
                      onClick={() => setForm({ ...form, color: c })}
                      className={`w-5 h-5 rounded-full border-2 transition-all ${form.color === c ? 'border-white scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c }}
                      title={c}
                    />
                  ))}
                </div>
                <input
                  type="color"
                  value={form.color}
                  onChange={e => setForm({ ...form, color: e.target.value })}
                  className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent"
                  title="Custom color"
                />
              </div>
            </div>

            {/* Active toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setForm({ ...form, is_active: !form.is_active })}
                className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors ${form.is_active ? 'bg-violet-600' : 'bg-zinc-600'}`}
              >
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${form.is_active ? 'translate-x-4' : 'translate-x-1'}`} />
              </button>
              <span className="text-xs text-zinc-400">{form.is_active ? 'Active (visible on site)' : 'Inactive (hidden)'}</span>
            </div>

            {/* Save/Cancel */}
            <div className="flex gap-2 pt-1">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-xs rounded font-medium transition-colors disabled:opacity-50"
              >
                <Save className="w-3 h-3" />
                {saving ? 'Saving...' : editingId === 'new' ? 'Add Class' : 'Save Changes'}
              </button>
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 text-xs rounded transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule List */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {loading && schedules.length === 0 && (
          <div className="text-center py-8 text-zinc-500 text-xs">
            <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
            Loading schedules...
          </div>
        )}

        {!loading && schedules.length === 0 && editingId === null && (
          <div className="text-center py-8">
            <Calendar className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-500 text-xs mb-3">No class schedules yet.</p>
            <div className="flex flex-col gap-2 items-center">
              <button
                onClick={handleAdd}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white rounded transition-colors"
              >
                <Plus className="w-3 h-3" />
                Add Your First Class
              </button>
              <button
                onClick={handleSeed}
                disabled={saving}
                className="text-xs text-zinc-500 hover:text-zinc-300 underline"
              >
                Or seed default classes from template
              </button>
            </div>
          </div>
        )}

        {schedules.map((schedule) => (
          <div
            key={schedule.id}
            className={`rounded-lg border transition-all ${
              schedule.is_active
                ? 'bg-zinc-800 border-zinc-700 hover:border-zinc-600'
                : 'bg-zinc-900 border-zinc-800 opacity-60'
            }`}
          >
            {/* Card header */}
            <div className="flex items-center gap-2 px-3 py-2.5">
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: schedule.color || '#4f46e5' }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-white truncate">{schedule.name}</span>
                  {!schedule.is_active && (
                    <span className="text-xs text-zinc-500 bg-zinc-700 px-1 rounded">hidden</span>
                  )}
                  {schedule.level && schedule.level !== 'All Levels' && (
                    <span
                      className="text-xs px-1.5 py-0.5 rounded text-white"
                      style={{ backgroundColor: LEVEL_COLORS[schedule.level] || '#6366f1', fontSize: '10px' }}
                    >
                      {schedule.level}
                    </span>
                  )}
                </div>
                <p className="text-xs text-zinc-500 truncate">{schedule.days} · {schedule.time}</p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setExpandedId(expandedId === schedule.id ? null : schedule.id)}
                  className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
                  title="Details"
                >
                  {expandedId === schedule.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
                <button
                  onClick={() => handleEdit(schedule)}
                  disabled={editingId !== null}
                  className="p-1 text-zinc-500 hover:text-violet-400 transition-colors disabled:opacity-30"
                  title="Edit"
                >
                  <Edit2 className="w-3 h-3" />
                </button>
                <button
                  onClick={() => handleDelete(schedule.id, schedule.name)}
                  disabled={saving}
                  className="p-1 text-zinc-500 hover:text-red-400 transition-colors disabled:opacity-30"
                  title="Delete"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Expanded details */}
            {expandedId === schedule.id && (
              <div className="px-3 pb-3 pt-0 border-t border-zinc-700/50 space-y-1.5 mt-1">
                {schedule.instructor && (
                  <div className="flex items-center gap-1.5 text-xs text-zinc-400">
                    <User className="w-3 h-3 text-zinc-500" />
                    <span>{schedule.instructor}</span>
                  </div>
                )}
                {schedule.description && (
                  <p className="text-xs text-zinc-400 leading-relaxed">{schedule.description}</p>
                )}
                {schedule.capacity && (
                  <div className="flex items-center gap-1.5 text-xs text-zinc-400">
                    <Users className="w-3 h-3 text-zinc-500" />
                    <span>Max {schedule.capacity} participants</span>
                  </div>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={() => handleToggleActive(schedule)}
                    disabled={saving}
                    className={`text-xs px-2 py-0.5 rounded transition-colors ${
                      schedule.is_active
                        ? 'bg-zinc-700 hover:bg-red-900/40 text-zinc-300 hover:text-red-300'
                        : 'bg-zinc-700 hover:bg-green-900/40 text-zinc-300 hover:text-green-300'
                    }`}
                  >
                    {schedule.is_active ? 'Hide from site' : 'Show on site'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer hint */}
      {schedules.length > 0 && (
        <div className="px-3 py-2 border-t border-white/10">
          <p className="text-xs text-zinc-600 text-center">
            Changes appear live on your website automatically.
          </p>
        </div>
      )}
    </div>
  );
}
